/* eslint-disable no-param-reassign */
import type React from 'react';

import isEqual from 'lodash/isEqual';

import { ThumbnailProps } from '@lumapps/lumx/react';
import createSlice, { PayloadAction } from '@lumapps/redux/createSlice';
import { ObjectValues } from '@lumapps/utils/types/ObjectValues';

export interface ImageLightboxState {
    /** Index of all images by their unique key */
    images: ThumbnailProps[];
    /** Index of the image to show by default in the slideshow */
    slideshowActiveIndex?: number;
    /** HTML Element to focus when the lightbox closes */
    parentElement: React.MutableRefObject<HTMLElement | null>;
    /** Lightbox open state */
    isOpen: boolean;
}

export const initialState: ImageLightboxState = {
    isOpen: false,
    images: [],
    parentElement: { current: null },
};

export const { reducer, actions } = createSlice({
    domain: 'image-lightbox-provider',
    initialState,
    reducers: {
        /** Remove images from the global slideshow using their keys */
        unregister(state: ImageLightboxState, action: PayloadAction<ThumbnailProps[]>) {
            for (const unregisteredImage of action.payload) {
                const indexOf = state.images.findIndex((image) => isEqual(image, unregisteredImage));
                if (indexOf >= 0) {
                    state.images.splice(indexOf, 1);
                }
            }
        },
        /** Add images by key into the global slideshow */
        register(state: ImageLightboxState, action: PayloadAction<ImageLightboxState['images']>) {
            state.images.push(...action.payload);
        },
        /** Open the lightbox and set to images to display, the default active image and the parent element */
        open(
            state: ImageLightboxState,
            action: PayloadAction<{ parentElement: HTMLElement; activeImage?: ThumbnailProps }>,
        ) {
            const { activeImage, parentElement } = action.payload;

            // Get index of image
            const activeIndex = activeImage ? state.images.findIndex((image) => isEqual(image, activeImage)) : -1;
            state.slideshowActiveIndex = Math.max(activeIndex, 0);
            state.parentElement.current = parentElement;
            state.isOpen = true;
        },
        /** Close the lightbox and clear unnecessary state */
        close(state: ImageLightboxState) {
            state.isOpen = false;
        },
    },
});

// Union type of all action types
export type Actions = ReturnType<ObjectValues<typeof actions>>;
